// ** React Imports
import { useEffect, useMemo, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { login } from "store/modules/auth/actions/login.action";
import {
	setActiveTab,
	clearRegisterForm,
	setFakeUserData,
	setCurrentStep,
	setRegistrationId,
	setEmail,
} from "store/modules/auth";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import md5 from "md5";
import loadable from "@loadable/component";

// ** Login Components
import UsernameInput from "./components/UsernameInput";
import PasswordInput from "./components/PasswordInput";
import { ButtonLanding } from "components/layout/Button/ButtonLanding";
import PasswordRecoverButton from "./components/PasswordRecoverButton";
import Block from "components/layout/Block";
import { GoogleOAuthButton } from "containers/Oauth/buttons/GoogleOAuthButton";
import { TwitterOAuthButton } from "containers/Oauth/buttons/TwitterOAuthButton";

// ** Custom Hooks
import { useFingerprint } from "hooks/useFingerprint";

// ** Utils & Helpers
import api from "helpers/ApiClient";

// ** Config
import configGlobal from "configuration";
import { setIsRedirectedFromLanding } from "store/modules/global-common.slice";

//** Register Components
const SendRegistrationEmailButton = loadable(
	() => import("containers/Landing/components/SendRegistrationEmailButton"),
	{ ssr: false }
);

// #####################################################

const Login = ({ oAuth, setOAuth, additionalContent = null }) => {
	const history = useHistory();

	const dispatch = useDispatch();

	const errorStatusCode = useSelector((state) => state.authLogin.errorCode);

	const [loginLoading, setLoginLoading] = useState(false);
	const [username, setUsername] = useState("");
	const [usernameError, setUsernameError] = useState(false);
	const [password, setPassword] = useState("");
	const [passwordError, setPasswordError] = useState(false);
	const [
		showSendRegistrationEmailButton,
		setShowSendRegistrationEmailButton,
	] = useState(false);

	const { getFingerprint } = useFingerprint();

	const language = useSelector((state) => state.globalCommon.language);

	const { t } = useTranslation(["landing"]);

	const isClient = useSelector((state) => state.globalCommon.isClient);

	let firebaseHashObject = null;

	if (isClient && localStorage) {
		try {
			firebaseHashObject = localStorage.getItem(
				configGlobal.firebase.localStorage.itemName
			);
		} catch {
			// localStorage access failed, firebaseHashObject remains null
		}
	}

	const activateFcmToken = (firebaseHashObject) => {
		const firebaseHash = firebaseHashObject
			? JSON.parse(firebaseHashObject)?.token
			: null;

		return api.put(`/setting/fcm/activate`, {
			id: null,
			hash: firebaseHash ? md5(firebaseHash) : undefined,
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (username === "") {
			setUsernameError(true);
			return;
		}
		if (password === "") {
			setPasswordError(true);
			return;
		}

		if (username && password) {
			setLoginLoading(true);
			setUsernameError(false);
			setPasswordError(false);

			const fingerprintRes = await getFingerprint();
			dispatch(
				login({ username, password, fingerprint: fingerprintRes })
			).then((res) => {
				const {
					id,
					email,
					hash,
					avatar,
					login,
					city_id,
					city,
					city_is_spam,
					country,
					region,
					region_name,
					sex,
					age,
					birthday,
					sms,
				} = res?.payload?.result || {};

				dispatch(clearRegisterForm());

				setLoginLoading(false);

				if (res.payload.isTokenExists) {
					if (firebaseHashObject) {
						activateFcmToken(firebaseHashObject);
					}
				} else {
					if (sms?.number) {
						history.push(
							`/registration-code/${login}/${id}/${hash}`
						);
					} else if (sms) {
						history.push(
							`/registration-phone/${login}/${id}/${hash}`
						);
					} else if (res?.payload?.result) {
						dispatch(
							setFakeUserData({
								avatar,
								login,
								city: city_id,
								cityName: city,
								city_is_spam,
								country,
								region,
								regionName: region_name,
								sex,
								user_data: {
									age,
									birthday,
								},
							})
						);
						dispatch(setCurrentStep(6));
						dispatch(setIsRedirectedFromLanding(true));
						dispatch(setRegistrationId(id));
						dispatch(setEmail(email));
						history.push(
							`/registration-verify-email?uuid=${Date.now()}`
						);
					}
				}
			});
			setPassword("");
		}
	};

	const location = useLocation();

	// ** Efekt odpowiadający za aktywację zakładki odzyskiwania hasła
	// ** na podstawie danych przekaznych w 'location.state'
	useEffect(() => {
		const { activeLast } = location.state || {};

		if (activeLast === true) {
			dispatch(setActiveTab("recover"));
		}
	}, [dispatch]);

	useEffect(() => {
		if (errorStatusCode >= 1000) {
			setShowSendRegistrationEmailButton(true);
		}
	}, [errorStatusCode]);

	const loginButtonText = useMemo(() => {
		const text = t("landing:login.action");
		return text;
	}, [language]);

	useEffect(() => {
		if (location.state?.action === "registerAfterOAuth") {
			dispatch(setActiveTab("register"));

			setOAuth({
				...oAuth,
				active: true,
				email: location.state?.mail,
				token: location.state?.token,
				provider: location.state?.provider,
				loading: false,
			});
			history.replace({ ...location, state: null });
		}
	}, [dispatch, history, location, setOAuth]);
	// #####################################################

	return (
		<>
			<form onSubmit={handleSubmit} autoComplete="on">
				{/* FAKE INPUT - Dla wyłączenia automatycznego wstawiania zapamiętanych danych w Chrome */}
				<input
					type="text"
					name="username"
					style={{ display: "none" }}
					autoComplete="username"
				/>
				{/* FAKE INPUT - Dla wyłączenia automatycznego wstawiania zapamiętanych danych w Chrome */}
				<input
					type="password"
					name="password"
					autoComplete="current-password"
					style={{ display: "none" }}
				/>
				{additionalContent}
				<UsernameInput
					username={username}
					setUsername={setUsername}
					usernameError={usernameError}
					setUsernameError={setUsernameError}
					setPasswordError={setPasswordError}
					setShowSendRegistrationEmailButton={
						setShowSendRegistrationEmailButton
					}
				/>
				<PasswordInput
					password={password}
					setPassword={setPassword}
					passwordError={passwordError}
					setPasswordError={setPasswordError}
					setUsernameError={setUsernameError}
				/>
				<ButtonLanding disabled={loginLoading} type="submit">
					{loginButtonText}
				</ButtonLanding>
			</form>

			{showSendRegistrationEmailButton && username && (
				<SendRegistrationEmailButton
					email={username}
					remainingTime={errorStatusCode - 1000}
				/>
			)}
			{location?.state?.action === "activationOAuth" &&
				location?.state?.mail && (
					<SendRegistrationEmailButton
						email={location?.state?.mail}
						remainingTime={location?.state?.lastSend - 1000}
					/>
				)}

			<PasswordRecoverButton />

			<Block flex column mt={4}>
				<GoogleOAuthButton
					oAuth={oAuth}
					setOAuth={setOAuth}
					firebaseHashObject={firebaseHashObject}
				/>
				<TwitterOAuthButton
					oAuth={oAuth}
					setOAuth={setOAuth}
					firebaseHashObject={firebaseHashObject}
				/>
			</Block>
		</>
	);
};

// #####################################################

export default Login;

// #####################################################
